/* Thanh toán theo đợt — mọi khách đang trả góp tại cơ sở: đợt quá hạn, đến hạn, chờ quầy thu (Trưởng phòng / thu ngân / tư vấn viên theo dõi) */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Switch, Tag } from 'antd';
import type { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlarmClock, CalendarClock, Hourglass, Wallet, Search } from 'lucide-react';
import { Kpi } from '../Telesales/dashboard/ui';
import { PAY_STEP_ST, payRows, payStepText, vnd, type PayRow, type Visit } from './logic';
import { useTuVan } from './store';
import { Card, DoctorGate, RTable, Shell, useMe, useNow, who } from './shared';

type Filter = 'need' | 'overdue' | 'due_soon' | 'requested' | 'open' | 'paid';
interface Row { v: Visit; r: PayRow; n: number }

export default function ThanhToanDotPage() {
  const navigate = useNavigate();
  const now = useNow();
  const me = useMe();
  const { visits, branchId } = useTuVan();
  const [filter, setFilter] = useState<Filter>('need');
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');

  const all = useMemo<Row[]>(() => visits
    .filter((v) => v.branchId === branchId && v.payPlan?.length)
    .flatMap((v) => payRows(v, now).map((r) => ({ v, r, n: v.payPlan!.length }))), [visits, branchId, now]);
  const sumOf = (xs: Row[]) => xs.reduce((s, x) => s + x.r.amount - x.r.paid, 0);
  const by = (st: PayRow['state']) => all.filter((x) => x.r.state === st);
  const open = all.filter((x) => x.r.state !== 'paid');
  const pick: Record<Filter, (x: Row) => boolean> = {
    need: (x) => x.r.state === 'overdue' || x.r.state === 'due_soon' || x.r.state === 'requested',
    overdue: (x) => x.r.state === 'overdue',
    due_soon: (x) => x.r.state === 'due_soon',
    requested: (x) => x.r.state === 'requested',
    open: (x) => x.r.state !== 'paid',
    paid: (x) => x.r.state === 'paid',
  };
  const kw = q.trim().toLowerCase();
  const rows = all
    .filter(pick[filter])
    .filter((x) => !mine || x.v.consultant === me.id)
    .filter((x) => !kw || `${x.v.cust.name} ${x.v.cust.code} ${x.v.cust.phone}`.toLowerCase().includes(kw))
    .sort((a, b) => (a.r.due ?? Infinity) - (b.r.due ?? Infinity) || a.r.no - b.r.no);

  const columns: ColumnType<Row>[] = [
    {
      title: 'Khách', key: 'cust',
      render: (_, { v }) => (
        <button type="button" onClick={() => navigate(`/coc-tai-co-so?v=${v.id}`)} className="text-left">
          <div className="font-semibold text-[#3e91ab] hover:underline">{v.cust.name}</div>
          <div className="text-xs text-gray-500">{v.cust.code} · {v.cust.phone}</div>
        </button>
      ),
    },
    { title: 'Đợt', key: 'no', width: 70, render: (_, { r, n }) => <b>{r.no}/{n}</b> },
    { title: 'Hạn', key: 'due', width: 110, render: (_, { r }) => (r.due ? dayjs(r.due).format('DD/MM/YYYY') : 'Chưa hẹn ngày') },
    { title: 'Số tiền', key: 'amt', align: 'right', render: (_, { r }) => <span className="tabular-nums"><b>{vnd(r.amount)}</b>{r.paid > 0 && r.paid < r.amount && <div className="text-xs text-gray-500">đã thu {vnd(r.paid)}</div>}</span> },
    { title: 'Trạng thái', key: 'st', render: (_, { r }) => <Tag color={PAY_STEP_ST[r.state].color} className="!m-0">{payStepText(r)}</Tag> },
    { title: 'Phiếu', key: 'q', render: (_, { v }) => <span className="text-xs text-gray-500">{v.quote?.code ?? '—'}</span> },
    { title: 'Tư vấn viên', key: 'tv', render: (_, { v }) => (v.consultant ? who(v.consultant) : '—') },
    { title: '', key: 'act', width: 90, align: 'right', render: (_, { v }) => <Button size="small" onClick={() => navigate(`/coc-tai-co-so?v=${v.id}`)}>Mở</Button> },
  ];

  return (
    <Shell title="Thanh toán theo đợt" sub="Khách trả góp tại cơ sở: đợt quá hạn, đến hạn, chờ quầy thu — bấm khách để mở kế hoạch và gửi quầy thu">
      <DoctorGate>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi icon={<AlarmClock size={15} />} label="Quá hạn" value={by('overdue').length} tone="red" alert={by('overdue').length > 0}
            active={filter === 'overdue'} onClick={() => setFilter('overdue')} sub={`Còn phải thu ${vnd(sumOf(by('overdue')))}`} />
          <Kpi icon={<CalendarClock size={15} />} label="Đến hạn ≤ 3 ngày" value={by('due_soon').length} tone="amber"
            active={filter === 'due_soon'} onClick={() => setFilter('due_soon')} sub={`Cần thu ${vnd(sumOf(by('due_soon')))}`} />
          <Kpi icon={<Hourglass size={15} />} label="Chờ quầy thu" value={by('requested').length} tone="sky"
            active={filter === 'requested'} onClick={() => setFilter('requested')} sub={`${vnd(sumOf(by('requested')))} đã gửi quầy`} />
          <Kpi icon={<Wallet size={15} />} label="Còn phải thu" value={new Set(open.map((x) => x.v.id)).size} tone="teal"
            active={filter === 'open'} onClick={() => setFilter('open')} sub={<>khách · tổng <b>{vnd(sumOf(open))}</b> · {open.length} đợt</>} />
        </div>
        <Card
          title={({ need: 'Cần xử lý: quá hạn, đến hạn, chờ quầy thu', overdue: 'Đợt quá hạn', due_soon: 'Đợt đến hạn trong 3 ngày', requested: 'Đợt chờ quầy thu', open: 'Mọi đợt chưa thu', paid: 'Đợt đã thu' } as Record<Filter, string>)[filter]}
          extra={(
            <span className="flex flex-wrap items-center gap-3">
              <span className="flex gap-1">
                {(['need', 'open', 'paid'] as Filter[]).map((f) => (
                  <Button key={f} size="small" type={filter === f ? 'primary' : 'default'} onClick={() => setFilter(f)}>{{ need: 'Cần xử lý', open: 'Chưa thu', paid: 'Đã thu' }[f as 'need' | 'open' | 'paid']}</Button>
                ))}
              </span>
              <Input allowClear size="small" prefix={<Search size={13} className="text-gray-400" />} placeholder="Tên, mã khách, SĐT" value={q} onChange={(e) => setQ(e.target.value)} className="!w-[200px]" />
              <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"><Switch size="small" checked={mine} onChange={setMine} />Khách của tôi</span>
            </span>
          )}
        >
          <RTable rowKey={(x) => `${x.v.id}-${x.r.no}`} columns={columns} dataSource={rows} scrollX={900} empty="Không có đợt nào" />
        </Card>
      </DoctorGate>
    </Shell>
  );
}
